import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { squeezoService } from '../services/squeezoService';
import { useWebSocket } from '../contexts/WebSocketContext';
import { TZ } from '../utils/marketTime';

interface SentimentControlWidgetProps {
  onRemove: () => void;
}

export const SentimentControlWidget: React.FC<SentimentControlWidgetProps> = ({ onRemove }) => {
  const [currentValue, setCurrentValue] = useState<number>(50);
  const [inputValue, setInputValue] = useState<number>(50);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();

  // Fetch initial value
  const { data, isLoading } = useQuery({
    queryKey: ['squeezo', 'latest'],
    queryFn: () => squeezoService.getLatest(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Update local state when data changes
  useEffect(() => {
    if (data?.data?.value !== undefined) {
      setCurrentValue(data.data.value);
      setInputValue(data.data.value);
      setLastUpdate(data.data.created_at);
    }
  }, [data]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    return subscribe('jdash.squeezo', (payload: any) => {
      if (payload.data?.value !== undefined) {
        setCurrentValue(payload.data.value);
        setInputValue(payload.data.value);
        setLastUpdate(payload.data.created_at);
      }
    });
  }, [subscribe]);

  // Create entry mutation
  const createMutation = useMutation({
    mutationFn: (value: number) => squeezoService.createEntry(value),
    onSuccess: () => {
      setStatus('success');
      queryClient.invalidateQueries({ queryKey: ['squeezo'] });
      setTimeout(() => setStatus('idle'), 1500);
    },
    onError: () => {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    },
  });

  const handleSubmit = () => {
    if (inputValue < 0 || inputValue > 100) return;
    if (inputValue === currentValue) return; // No change
    createMutation.mutate(inputValue);
  };

  const increment = () => {
    setInputValue((v) => Math.min(100, v + 1));
  };

  const decrement = () => {
    setInputValue((v) => Math.max(0, v - 1));
  };

  const incrementBy = (amount: number) => {
    setInputValue((v) => Math.min(100, Math.max(0, v + amount)));
  };

  // Color based on value
  const getValueColor = (value: number) => {
    if (value >= 66) return 'text-bull-blue';
    if (value <= 33) return 'text-bear-red';
    return 'text-accent-yellow';
  };

  const hasChanged = inputValue !== currentValue;
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Sentiment</h3>
          {isConnected && (
            <span className="w-1.5 h-1.5 rounded-full bg-bull-blue" title="Live" />
          )}
        </div>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Content - Compact row per model */}
      <div className="flex-1 flex flex-col p-2 gap-2 overflow-auto">
        {/* Squeezo row */}
        <div className="flex items-center gap-2">
          {/* Model name + last update */}
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-medium text-gray-200">Squeezo</span>
            {lastUpdate && (
              <span className="text-[10px] text-gray-500">{formatTime(lastUpdate)}</span>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Input with arrows */}
          <div className="flex items-center">
            {/* Decrement buttons */}
            <button
              onClick={() => incrementBy(-10)}
              className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light rounded-l border border-dark-border border-r-0 transition-colors"
              title="-10"
            >
              −−
            </button>
            <button
              onClick={decrement}
              className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light border-y border-dark-border transition-colors"
              title="-1"
            >
              −
            </button>

            {/* Input */}
            <input
              type="number"
              min="0"
              max="100"
              value={inputValue}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v)) setInputValue(Math.min(100, Math.max(0, v)));
              }}
              className={`w-12 px-1 py-1 text-center text-sm font-medium border-y border-dark-border bg-dark-surface focus:outline-none ${
                isLoading ? 'text-gray-500' : getValueColor(inputValue)
              }`}
            />

            {/* Increment buttons */}
            <button
              onClick={increment}
              className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light border-y border-dark-border transition-colors"
              title="+1"
            >
              +
            </button>
            <button
              onClick={() => incrementBy(10)}
              className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light rounded-r border border-dark-border border-l-0 transition-colors"
              title="+10"
            >
              ++
            </button>
          </div>

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!hasChanged || createMutation.isPending}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
              hasChanged && !createMutation.isPending
                ? 'bg-bull-blue hover:bg-bull-blue/80 text-white'
                : 'bg-dark-border text-gray-500 cursor-not-allowed'
            } ${status === 'success' ? 'bg-bull-blue/50' : ''} ${status === 'error' ? 'bg-bear-red/50' : ''}`}
          >
            {createMutation.isPending ? '...' : status === 'success' ? '✓' : 'Set'}
          </button>
        </div>

        {/* Add more models here in the future */}
      </div>
    </div>
  );
};
